import {FeesArraySelector, FeeStartDate} from './../../../../../assets/descriptors/fees-form.descriptor';
import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

import {from, Observable, of, Subscription} from 'rxjs';

import {
    ChargeGroupModel,
    ChargeScheduleModel,
    ChargeSchedulesService,
    HttpStatusCodesEnum,
    ReportsService,
    TradingAccountFilterModel,
    TradingAccountReportInterface,
    TradingAccountReportModel,
    TradingAccountStatusesEnum,
    FeeValidityEnum
} from '@spa/api';

import {AlertsService} from 'appShared/services';
import {FilterControlInterface, FormInterface} from 'appShared/interfaces';
import {FormComponent, ItemsPerPageSelector} from 'appShared/widgets';
import {DialogActionsEnum, DialogComponent, DialogConfigInterface} from '../../../../shared/components/dialog';

import {BulkFeesForm} from '../../forms';

import {AddFeeMessages} from '../../../../../assets/messages';
import {FeeEndDate} from 'appAssets/descriptors';
import {FormArray} from '@angular/forms';
import * as moment from 'moment';
import {BaseControl, FilterControl, SelectControl} from "appShared/classes";
import {concatMap, map, switchMap} from "rxjs/operators";
import {AlertTypesEnum, ComponentTypesEnum} from "appShared/enums";
import {environment} from "appEnv/environment";

@Component({
    selector: 'app-bulk-add-fee',
    templateUrl: './bulk-add-fee.component.html',
    styleUrls: ['./bulk-add-fee.component.scss']
})
export class BulkAddFeeComponent extends DialogComponent<BulkAddFeeComponent> implements OnInit, OnDestroy, FormInterface {
    private readonly assignFeeSubscriptions: Subscription[];
    private getFeeFailedNotifierSubscriptions: Subscription[];

    public feesForm: BulkFeesForm;
    public title = 'Add New Fee Plan';
    public showDateWarning = false;
    public selectedFilters: { label: string, value: (string | number) }[];
    public isLoading = false;
    public currentItem = 0;
    public totalItems = 0;

    constructor(
        private reportsService: ReportsService,
        private chargeSchedulesService: ChargeSchedulesService,
        private alertsService: AlertsService,
        @Inject(MAT_DIALOG_DATA) public data: {
            accounts: TradingAccountReportModel[],
            ignoreSelected: boolean,
            allFeeGroups: ChargeGroupModel[],
            filterControls: FilterControl[],
            filterModelFunction: (data: { [p: string]: number | string | boolean }) => TradingAccountFilterModel,
            config: DialogConfigInterface,
            isValidityUpdate: boolean,
            modelToUpdate: ChargeScheduleModel
        },
        public dialogRef: MatDialogRef<BulkAddFeeComponent>
    ) {
        super(dialogRef);

        this.config = data.config;
        this.getFeeFailedNotifierSubscriptions = [];
        this.assignFeeSubscriptions = [];
        if (data.isValidityUpdate) {
            this.title = 'Edit Fee Plan Validity';
        }
    }

    private addRegularControl(control: BaseControl): void {
        this.selectedFilters.push({label: control.placeholder, value: control.value});
    }

    private initSelectedFilters() {
        this.selectedFilters = [];
        this.data.filterControls.filter(f => f.shown).forEach(filterControl => {
            filterControl.controls.filter(c => c.name !== ItemsPerPageSelector).forEach(control => {
                if (control.value) {
                    if (control.componentType === ComponentTypesEnum.Select || control.componentType === ComponentTypesEnum.Autocomplete) {
                        const selectControl = control as SelectControl;
                        const selectedOption = selectControl.options?.find(o => o.value === control.value);

                        if (selectedOption) {
                            this.selectedFilters.push({label: control.placeholder, value: selectedOption.label});
                        } else {
                            this.addRegularControl(control);
                        }
                    } else if (control.componentType === ComponentTypesEnum.Datepicker) {
                        const parsedDate = moment(control.value).format(environment.dateFormatLocalDate);
                        this.selectedFilters.push({label: control.placeholder, value: parsedDate});
                    } else {
                        this.addRegularControl(control);
                    }
                }
            })
        })
    }

    private getAlreadyAssignedAccounts(): Observable<string[]> {
        const feeReferenceId = this.feesForm.getSelectedFeeReferenceId();
        const data: { [p: string]: number | string | boolean } = {};
        data[TradingAccountFilterModel.getAccountStatusSelector()] = TradingAccountStatusesEnum.Active;
        data[TradingAccountFilterModel.getChargeScheduleReferenceIdSelector()] = feeReferenceId;
        data[TradingAccountFilterModel.getFeeValiditySelector()] = FeeValidityEnum.Assigned;

        const filters = this.data.filterModelFunction(data);

        return this.reportsService.getTradingAccountsReport(filters)
            .pipe(map(response => {
                if (response.status !== HttpStatusCodesEnum.Ok || !response.data) {
                    return [];
                }

                const accounts = response.data as TradingAccountReportInterface[];

                return accounts.map(a => a.account_reference_id);
            }));
    }

    ngOnInit(): void {
        this.initForm();
    }

    ngOnDestroy(): void {
        this.feesForm.getFeesSubscriptions.map((subscription: Subscription) => {
            return (subscription && subscription.unsubscribe());
        });

        this.feesForm.feeTypeControlValueChangesSubscriptions.map((subscription: Subscription) => {
            return (subscription && subscription.unsubscribe());
        });

        this.getFeeFailedNotifierSubscriptions.map((subscription: Subscription) => {
            return (subscription && subscription.unsubscribe());
        });

        if (this.assignFeeSubscriptions) {
            this.assignFeeSubscriptions.forEach(s => s?.unsubscribe());
        }

        if (this.feesForm.feeControlChangeSubscription) {
            this.feesForm.feeControlChangeSubscription.unsubscribe();
        }
    }

    initForm(): void {
        if (!this.data.accounts || this.data.accounts.length === 0) {
            this.alertsService.addAlert('Please select accounts', AlertTypesEnum.Error);
            this.dialogRef.close(false);

            return;
        }

        this.initSelectedFilters();

        this.feesForm = new BulkFeesForm(this.chargeSchedulesService);

        this.feesForm.initForm(this.data.allFeeGroups, this.data.isValidityUpdate, this.data.modelToUpdate);

        this.getFeeFailedNotifierSubscriptions.push(this.feesForm.getFeesFailedNotifier.subscribe(() => {
            this.alertsService.addAlert(AddFeeMessages.GetFeesFailed.message);
        }));

        const formGroupsArray = this.feesForm.form.get(FeesArraySelector) as FormArray;
        const endDateControl = formGroupsArray.controls[0].get(FeeEndDate);
        const endDateSubscription = endDateControl.valueChanges.subscribe((endDate: string) => {
            this.checkPastDate(endDate);
        });

        const startDateControl = formGroupsArray.controls[0].get(FeeStartDate);
        const startDateSubscription = startDateControl.valueChanges.subscribe((startDate: string) => {
            this.checkPastDate(startDate);
        });

        this.assignFeeSubscriptions.push(endDateSubscription)
        this.assignFeeSubscriptions.push(startDateSubscription)
    }

    getFormMessages(controlLabel?: string, controlLength?: number): { [p: string]: string } {

        return {
            ...FormComponent.getFormMessages(controlLabel, controlLength),
            [AddFeeMessages.WrongEndDateValue.key]: AddFeeMessages.WrongEndDateValue.message,
            [AddFeeMessages.EqualDataValue.key]: AddFeeMessages.EqualDataValue.message
        };
    }

    validateForm(): boolean {
        return this.feesForm.validateForm(this.getFormMessages);
    }

    onFormSubmit(action?: number): void {
        if (action !== DialogActionsEnum.Confirm) {
            this.dialogRef.close(!this.config.process.failed);
        } else {
            if (this.validateForm()) {
                let accountsRequest = of(this.data.accounts);

                if (this.data.ignoreSelected) {
                    const filterObject = this.data.filterControls.reduce(
                        (reducedObject: { [key: string]: number | string | boolean }, filterControl: FilterControlInterface) => {
                            filterControl.controls.map((control: BaseControl) => {
                                reducedObject[control.name] = control.value;
                            });

                            return reducedObject;
                        }, {});

                    filterObject[TradingAccountFilterModel.getAccountStatusSelector()] = TradingAccountStatusesEnum.Active;
                    const filters = this.data.filterModelFunction(filterObject);
                    accountsRequest = this.reportsService.getTradingAccountsReport(filters).pipe(map(accountsResponse => {
                        if (accountsResponse.status !== HttpStatusCodesEnum.Ok) {
                            this.alertsService.addAlert('Failed to fetch accounts');
                            this.dialogRef.close(false);
                            const empty: TradingAccountReportModel[] = []
                            return empty;
                        }

                        const accountInterfaces = accountsResponse.data as TradingAccountReportInterface[];
                        const accounts = accountInterfaces.map(a => new TradingAccountReportModel(a));

                        return accounts;
                    }))
                }

                this.isLoading = true;
                let errorCount = 0;
                let skippedCount = 0;

                const alreadyAssignedSubscription = this.getAlreadyAssignedAccounts().subscribe(alreadyAssignedAccounts => {
                    const accountsSubscription = accountsRequest
                        .pipe(switchMap(() => {
                            const accounts = this.data.accounts;

                            this.totalItems = accounts.length;

                            return from(accounts).pipe(concatMap(account => {
                                this.currentItem++;

                                const alreadyAssigned = alreadyAssignedAccounts.indexOf(account.accountReferenceId) >= 0;
                                const isUpdate = this.data.isValidityUpdate;

                                if (isUpdate && !alreadyAssigned) {
                                    skippedCount++;
                                    return of(null);
                                } else if (!isUpdate && alreadyAssigned) {
                                    skippedCount++;
                                    return of(null);
                                }
                                this.feesForm.updateModel = account.chargeSchedule;

                                return this.feesForm.createRequest(account.accountReferenceId);
                            }));

                        })).subscribe(
                            data => {
                                if (data.status !== HttpStatusCodesEnum.Ok) {
                                    errorCount++;
                                }
                            },
                            error => {

                            },
                            () => {

                                let message = `Processing complete `;
                                message += errorCount > 0 ? `with ${errorCount} error(s) ` : 'successfully ';
                                message += `for ${this.totalItems} account(s). `

                                let alertType = errorCount > 0 ? AlertTypesEnum.Error : AlertTypesEnum.Success

                                if (skippedCount > 0) {
                                    message += `${skippedCount} accounts skipped.`;
                                    alertType = errorCount > 0 ? AlertTypesEnum.Error : AlertTypesEnum.Warning;
                                }

                                this.alertsService.addAlert(message, alertType);

                                this.dialogRef.close(errorCount === 0);

                                this.isLoading = false;
                                this.currentItem = 0;
                                this.totalItems = 0;
                            });

                    this.assignFeeSubscriptions.push(accountsSubscription);
                });

                this.assignFeeSubscriptions.push(alreadyAssignedSubscription);
            }
        }
    }

    checkPastDate(endDate: string) {
        const updateModel = this.data.modelToUpdate;
        const today = moment(new Date().setHours(0, 0, 0, 0)).unix();
        this.showDateWarning = moment(endDate).unix() < today &&
            moment(endDate).unix() !== moment(updateModel == null ? null : updateModel.endDate).unix();
    }
}
